import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { plural } from '@biuro/shared';
import { Icon } from './ui.tsx';

const DEFAULT_ACCEPT = 'application/pdf,image/*';

/** Czy plik pasuje do `accept` (typy MIME, `typ/*` albo rozszerzenia `.pdf`); PDF rozpoznajemy też po rozszerzeniu. */
export function acceptsFile(f: File, accept: string): boolean {
  const type = f.type.toLowerCase(), name = f.name.toLowerCase();
  return accept.split(',').map((a) => a.trim().toLowerCase()).filter(Boolean).some((a) => {
    if (a.startsWith('.')) return name.endsWith(a);
    if (a.endsWith('/*')) return type.startsWith(a.slice(0, -1));
    if (a === 'application/pdf' && name.endsWith('.pdf')) return true;
    return type === a;
  });
}

/** Przeciągany jest plik (a nie tekst czy link z innej karty). */
export const draggingFiles = (e: DragEvent | globalThis.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');

/** Plik upuszczony obok strefy nie otwiera się w przeglądarce (nie gubi np. wpisanych danych formularza). */
export function useFileDropGuard() {
  useEffect(() => {
    const block = (e: globalThis.DragEvent) => { if (draggingFiles(e)) e.preventDefault(); };
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => {
      window.removeEventListener('dragover', block);
      window.removeEventListener('drop', block);
    };
  }, []);
}

/**
 * Strefa wyboru pliku: kliknięcie otwiera okno wyboru, a plik można też upuścić.
 * Bez `multiple` przekazuje tylko pierwszy plik. `busy` – tekst postępu; strefa nie przyjmuje wtedy plików.
 */
export function FileDrop({ id, label, multiple, disabled, busy, accept = DEFAULT_ACCEPT, onFiles, children }: {
  id: string; label: string; multiple?: boolean; disabled?: boolean; busy?: string; accept?: string;
  onFiles: (files: File[]) => void; children?: ReactNode;
}) {
  const [over, setOver] = useState(false);
  const [msg, setMsg] = useState('');
  const depth = useRef(0);
  const off = !!disabled || !!busy;

  const take = (list: File[]) => {
    if (!list.length) return;
    const ok = list.filter((f) => acceptsFile(f, accept));
    if (!ok.length) return setMsg(list.length === 1 ? 'Ten plik nie jest PDF-em ani obrazem.' : 'Te pliki nie są PDF-ami ani obrazami.');
    const skipped = list.length - ok.length;
    if (!multiple && ok.length > 1) setMsg(`Upuszczono ${ok.length} ${plural(ok.length, 'plik', 'pliki', 'plików')} – biorę pierwszy: ${ok[0].name}.`);
    else setMsg(skipped ? `Pominięto ${skipped} ${plural(skipped, 'plik', 'pliki', 'plików')} – to nie PDF-y ani obrazy.` : '');
    onFiles(multiple ? ok : [ok[0]]);
  };

  const enter = (e: DragEvent) => {
    if (!draggingFiles(e)) return;
    e.preventDefault();
    if (off) return;
    depth.current++;
    setOver(true);
  };
  const overZone = (e: DragEvent) => {
    if (!draggingFiles(e)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = off ? 'none' : 'copy';
  };
  const leave = (e: DragEvent) => {
    if (!draggingFiles(e)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (!depth.current) setOver(false);
  };
  const dropped = (e: DragEvent) => {
    if (!draggingFiles(e)) return;
    // Bez stopPropagation: otaczająca lista też musi zdjąć podświetlenie; `defaultPrevented` mówi jej, że plik już przyjęto.
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    if (!off) take(Array.from(e.dataTransfer.files || []));
  };

  return (
    <>
      <label className={`drop${over ? ' over' : ''}`} aria-disabled={off || undefined}
        onDragEnter={enter} onDragOver={overZone} onDragLeave={leave} onDrop={dropped}>
        <input id={id} type="file" accept={accept} multiple={multiple} disabled={off}
          onChange={(e) => { take(Array.from(e.target.files || [])); e.target.value = ''; }} />
        <Icon.upload />
        <span className="drop-label">{busy || label}</span>
        {children}
      </label>
      {msg && <div className="hint" role="status">{msg}</div>}
    </>
  );
}
